import { IpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import {
  type GraphPath,
  IPF_NETWORK_VIEWER,
  type NodeRecord,
} from '@ipfabric/utils';
import { hideTooltip } from 'components/Graph/commands/pathLookup/hideTooltip';
import { savePathDefaultLayout } from 'components/Graph/commands/savePathDefaultLayout';
import type { GraphCommandFactory } from 'components/Graph/model/GraphCommandContext';
import { record } from 'fp-ts';
import { pipe } from 'fp-ts/lib/pipeable';
import { isTopologyGraphMetadata } from '../GraphState';
import { getNodePositions } from '../helpers/getNodePositions';

export interface SaveUserDefinedLayoutCommand {
  path: GraphPath;
}

const filterNodesByPath = (path: GraphPath | null, nodes: NodeRecord) =>
  pipe(
    nodes,
    record.filter(({ parentPath }) => parentPath === path),
  );

export const saveUserDefinedLayout: GraphCommandFactory<
  SaveUserDefinedLayoutCommand
> = (cmdContext, dispatch) => async (command) => {
  const { graphMetadata, graph } = cmdContext.getState();
  if (!isTopologyGraphMetadata(graphMetadata)) {
    throw new IpfError(
      'INVALID_STATE',
      'cannot save default positions for path when not in topology mode',
    );
  }

  const nodesForPath =
    command.path === IPF_NETWORK_VIEWER
      ? graph.nodes
      : filterNodesByPath(command.path, graph.nodes);

  const collapsedNodeGroupsForPath = pipe(
    graph.collapsedNodeGroups,
    record.map((group) => ({
      ...group,
      nodes: filterNodesByPath(command.path, group.nodes),
    })),
    record.filter(({ nodes }) => Object.keys(nodes).length !== 0),
  );

  await cmdContext.api.request({
    url: '/graphs/default-positions',
    method: 'POST',
    data: [
      {
        groupBy: graphMetadata.parameters.groupBy,
        path: command.path,
        positions: getNodePositions(nodesForPath, collapsedNodeGroupsForPath),
      },
    ],
  });
  // Intentionally not paralelized with Promise.all(). We need to first save default positions,
  // otherwise we could have an inconsistent state in database for a moment - layout set to user defined
  // and no or stale saved user positions.
  await dispatch(savePathDefaultLayout, {
    path: command.path,
    layout: 'userDefined',
    groupBy: graphMetadata.parameters.groupBy,
  });

  cmdContext.showNotification({
    type: 'success',
    message:
      cmdContext.msg.canvasGraph.commands.saveUserDefinedLayout
        .savedSuccessfully,
  });
  cmdContext.updateState((draft) => {
    if (isTopologyGraphMetadata(draft.graphMetadata)) {
      draft.graphMetadata.nodesRepositioned.delete(command.path);
      const updatedPath = draft.graphMetadata.paths[command.path];
      if (updatedPath) {
        // Fixed noUncheckedIndexedAccess NIM-19408
        draft.graphMetadata.paths[command.path]!.defaultLayout = 'userDefined';
      }
    }
    draft.pathLayouts[graphMetadata.parameters.groupBy][command.path] =
      'userDefined';
  });
  dispatch(hideTooltip, { type: 'layoutButton' });
};
